import isElectron from '../utils/isElectron';

export type CloudCrateServiceId = 'spotify' | 'apple' | 'tidal';

interface CloudCrateSaveData {
  serviceId: string;
  playlistId?: string;
  playlistUrl?: string;
  trackCount: number;
  matchedCount: number;
  savedAt: Date;
}

/**
 * Service for managing Cloud Crate saves to streaming services.
 * Persists save state to the local Electron database.
 */
class CloudCrateServiceClass {
  // In-memory cache for faster checks (populated from IPC)
  private savedCache: Map<string, Set<string>> = new Map();

  /**
   * Get cache key for a crate
   */
  private getCacheKey(crateId: string): string {
    return crateId;
  }

  /**
   * Check if a crate has been saved to a specific service (sync check from cache)
   */
  hasSaved(crateId: string, serviceId: CloudCrateServiceId): boolean {
    const cached = this.savedCache.get(this.getCacheKey(crateId));
    return cached ? cached.has(serviceId) : false;
  }

  /**
   * Load saved services for a crate from the local database
   */
  async loadSavedServices(crateId: string): Promise<string[]> {
    if (!isElectron()) {
      return [];
    }

    try {
      const result = await window.api.invoke(
        'cloud-crates/get-saved-services',
        { crateId },
      );
      if (result && result.services) {
        const cacheKey = this.getCacheKey(crateId);
        this.savedCache.set(cacheKey, new Set(result.services));
        return result.services;
      }
    } catch (error) {
      console.error('[CloudCrate] Error getting saved services:', error);
    }
    return [];
  }

  /**
   * Record a save to a streaming service
   */
  async recordSave(
    crateId: string,
    serviceId: CloudCrateServiceId,
    data: {
      playlistId?: string;
      playlistUrl?: string;
      trackCount: number;
      matchedCount: number;
    },
  ): Promise<boolean> {
    if (!isElectron()) {
      console.warn('[CloudCrate] Not in Electron, save will not be persisted');
      // Still update local cache for UI consistency
      const cacheKey = this.getCacheKey(crateId);
      if (!this.savedCache.has(cacheKey)) {
        this.savedCache.set(cacheKey, new Set());
      }
      this.savedCache.get(cacheKey)!.add(serviceId);
      return true;
    }

    try {
      const result = await window.api.invoke('cloud-crates/record-save', {
        crateId,
        serviceId,
        playlistId: data.playlistId,
        playlistUrl: data.playlistUrl,
        trackCount: data.trackCount,
        matchedCount: data.matchedCount,
      });

      if (result && result.success) {
        const cacheKey = this.getCacheKey(crateId);
        if (!this.savedCache.has(cacheKey)) {
          this.savedCache.set(cacheKey, new Set());
        }
        this.savedCache.get(cacheKey)!.add(serviceId);
        return true;
      }
    } catch (error) {
      console.error('[CloudCrate] Error recording save:', error);
    }
    return false;
  }

  /**
   * Get all saves for a crate
   */
  async getSavesForCrate(crateId: string): Promise<CloudCrateSaveData[]> {
    if (!isElectron()) {
      return [];
    }

    try {
      const result = await window.api.invoke(
        'cloud-crates/get-saves-for-crate',
        { crateId },
      );
      if (result && result.saves) {
        return result.saves;
      }
    } catch (error) {
      console.error('[CloudCrate] Error getting saves for crate:', error);
    }
    return [];
  }

  /**
   * Remove a save record
   */
  async removeSave(
    crateId: string,
    serviceId: CloudCrateServiceId,
  ): Promise<boolean> {
    if (!isElectron()) {
      const cacheKey = this.getCacheKey(crateId);
      this.savedCache.get(cacheKey)?.delete(serviceId);
      return true;
    }

    try {
      const result = await window.api.invoke('cloud-crates/remove-save', {
        crateId,
        serviceId,
      });
      if (result && result.success) {
        const cacheKey = this.getCacheKey(crateId);
        this.savedCache.get(cacheKey)?.delete(serviceId);
        return true;
      }
    } catch (error) {
      console.error('[CloudCrate] Error removing save:', error);
    }
    return false;
  }

  /**
   * Clear the cache for a crate (useful when crate data changes)
   */
  clearCache(crateId?: string): void {
    if (crateId) {
      this.savedCache.delete(this.getCacheKey(crateId));
    } else {
      this.savedCache.clear();
    }
  }
}

export const CloudCrateService = new CloudCrateServiceClass();
